import { ChatReferenceImage } from "@indigo-components/components/Chat/ChatReferenceImage/ChatReferenceImage";
import { errorReporter } from "~/error-handling";
import type { ChatbotLinkContext } from "~/pages/appointments/components/chatbotLinks";
import type { AmaTableFigureImage } from "~/pydantic_models/services/models/chat_blocks";
import type { ChatBlockRendererProps } from "./types";
import { usePresignedReadUrls } from "./usePresignedReadUrls";

const ACCESS_ERROR = {
  message: "AMA table/figure image access failed",
  stage: "ama_table_figure_image_access",
};

/** Shape check only; which keys may be read is presign-read's decision. */
export function parseAmaTableFigureImages(
  data: Record<string, unknown> | null | undefined,
): AmaTableFigureImage[] | null {
  if (!Array.isArray(data?.images) || !data.images.length) return null;
  const images: AmaTableFigureImage[] = [];
  for (const raw of data.images) {
    if (!raw || typeof raw !== "object") return null;
    const image = raw as Partial<AmaTableFigureImage>;
    if (
      typeof image.s3_bucket !== "string" ||
      typeof image.s3_key !== "string" ||
      typeof image.label !== "string" ||
      typeof image.title !== "string" ||
      typeof image.page !== "number"
    )
      return null;
    images.push(image as AmaTableFigureImage);
  }
  return images;
}

function AmaTableFigureImageList({
  images,
  ctx,
}: {
  images: AmaTableFigureImage[];
  ctx: ChatbotLinkContext;
}) {
  const { urls, loading, retry } = usePresignedReadUrls(images, ACCESS_ERROR);
  return (
    <div className="flex flex-col gap-2">
      {images.map((image) => (
        <ChatReferenceImage
          key={image.s3_key}
          imageUrl={urls[image.s3_key]}
          loading={loading && !urls[image.s3_key]}
          caption={`${image.label}: ${image.title}`}
          altText={`${image.label} from the AMA Guides: ${image.title}`}
          sourceTitle={`AMA Guides, 5th Edition, page ${image.page}`}
          onSourceClick={
            ctx.onRequestAmaGuide
              ? () => void ctx.onRequestAmaGuide?.({ page: image.page })
              : undefined
          }
          onRetry={retry}
          bare
          onLoadError={() =>
            errorReporter.reportMessage(
              "AMA table/figure image display failed",
              {
                level: "warning",
                tags: {
                  feature: "indy",
                  stage: "ama_table_figure_image_display",
                },
              },
            )
          }
        />
      ))}
    </div>
  );
}

export function AmaTableFigureImagesBlock({
  block,
  ctx,
  fallback,
}: ChatBlockRendererProps) {
  const images = parseAmaTableFigureImages(block.data);
  if (!images) return <>{fallback}</>;
  return <AmaTableFigureImageList images={images} ctx={ctx} />;
}
